import { AVATAR_IDS, type AvatarId } from '@gmp/shared-types/browser';

/**
 * Original vector party characters. Each avatar is a body silhouette with its own palette; faces share one
 * expressive style so the cast reads as a family. The player color draws the ring, never the only cue.
 */
const BODIES: Record<AvatarId, { fill: string; shade: string; path: string; face?: [number, number] }> = {
  blob: { fill: '#ff6b9d', shade: '#d6457a', path: 'M50 12c22 0 36 16 36 38 0 24-14 38-36 38S14 74 14 50c0-22 14-38 36-38z' },
  bolt: { fill: '#ffd23f', shade: '#e0a800', path: 'M58 6L20 56h24l-8 38 44-54H54z', face: [50, 50] },
  cactus: { fill: '#5ad17a', shade: '#2f9e52', path: 'M40 14c0-6 20-6 20 0v26h10V28c0-6 12-6 12 0v24c0 8-6 12-12 12H60v24H40V64H30c-6 0-12-4-12-12V36c0-6 12-6 12 0v12h10z' },
  crown: { fill: '#ffb627', shade: '#d98a00', path: 'M14 34l18 14 18-30 18 30 18-14-8 50H22z', face: [50, 60] },
  dice: { fill: '#f4f1ff', shade: '#bdb4e6', path: 'M22 14h56a10 10 0 0110 10v52a10 10 0 01-10 10H22a10 10 0 01-10-10V24a10 10 0 0110-10z' },
  donut: { fill: '#ff9ecb', shade: '#e06aa5', path: 'M50 10a40 40 0 110 80 40 40 0 010-80z' },
  ghost: { fill: '#e8e6ff', shade: '#b8b2f0', path: 'M50 10c20 0 34 15 34 36v42l-11-8-11 8-12-8-12 8-11-8-11 8V46c0-21 14-36 34-36z' },
  moon: { fill: '#c8b6ff', shade: '#9a83f0', path: 'M62 10a40 40 0 1024 64A34 34 0 0162 10z', face: [44, 50] },
  octo: { fill: '#b06bff', shade: '#8240d6', path: 'M50 10c20 0 32 14 32 32 0 10-4 16-4 22l8 18-12-6-6 14-8-12-10 12-10-12-8 12-6-14-12 6 8-18c0-6-4-12-4-22 0-18 12-32 32-32z' },
  rocket: { fill: '#ff5a5f', shade: '#cc3036', path: 'M50 6c16 10 24 30 22 52l10 14-8 12-10-8H36l-10 8-8-12 10-14C26 36 34 16 50 6z' },
  toast: { fill: '#f2b56b', shade: '#c9813a', path: 'M24 34c-14 0-16-22 6-24 6-1 14 2 20 2s14-3 20-2c22 2 20 24 6 24v50H24z' },
  cube: { fill: '#3ddc97', shade: '#21a56b', path: 'M50 8l38 20v44L50 92 12 72V28z' },
  cloud: { fill: '#9ad8ff', shade: '#5aa9e6', path: 'M28 80c-14 0-20-12-16-22 2-6 8-10 14-10 0-16 12-26 26-26 12 0 22 8 24 20 12 0 20 8 20 19s-8 19-20 19z' },
  flame: { fill: '#ff7b2e', shade: '#d4500c', path: 'M50 6c4 16 30 26 30 52 0 18-14 32-30 32S20 76 20 58c0-14 8-22 14-28 0 10 4 16 10 18-2-16 2-30 6-42z' },
  gem: { fill: '#42e6e6', shade: '#16b0b8', path: 'M30 14h40l18 22-38 52-38-52z' },
  mush: { fill: '#ff6262', shade: '#d63a3a', path: 'M50 10c26 0 40 16 40 32 0 6-6 8-14 8H24c-8 0-14-2-14-8 0-16 14-32 40-32zM34 50h32v30c0 6-6 10-16 10s-16-4-16-10z', face: [50, 66] },
};

const MOODS = ['smile', 'grin', 'o', 'smug'] as const;

/** Avatars with clay artwork in /avatars/<id>.webp (all of them now); the vector characters below are the fallback. */
const ART_AVATARS: ReadonlySet<AvatarId> = new Set(AVATAR_IDS);

export function Avatar({ id, size = 48, color, mood, className = '' }: { id: AvatarId; size?: number; color?: string; mood?: (typeof MOODS)[number]; className?: string }) {
  if (ART_AVATARS.has(id)) {
    // Clay cut-outs stand on their own: no circle, ring or crop, just a soft shadow under the character.
    return (
      <span className={`inline-block shrink-0 align-middle ${className}`} style={{ width: size, height: size }} role="img" aria-label={`${id} avatar`}>
        <img
          src={`/avatars/${id}.webp`} alt="" width={size} height={size} draggable={false}
          className="block w-full h-full object-contain select-none"
          style={{ filter: `drop-shadow(0 ${Math.max(1, Math.round(size * 0.04))}px ${Math.max(1, Math.round(size * 0.05))}px rgba(0,0,0,0.35))` }}
        />
      </span>
    );
  }
  const b = BODIES[id] ?? BODIES.blob;
  const [fx, fy] = b.face ?? [50, 52];
  const m = mood ?? MOODS[(id.charCodeAt(0) + id.length) % MOODS.length];
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} role="img" aria-label={`${id} avatar`}>
      {color && <circle cx="50" cy="50" r="48" fill="none" stroke={color} strokeWidth="5" />}
      <g transform={color ? 'translate(9 9) scale(0.82)' : undefined}>
        <path d={b.path} fill={b.shade} transform="translate(0 4)" />
        <path d={b.path} fill={b.fill} />
        {id === 'dice' && (
          <g fill="#2a2140">
            <circle cx="28" cy="28" r="5" /><circle cx="72" cy="72" r="5" /><circle cx="72" cy="28" r="5" />
          </g>
        )}
        {id === 'donut' && <circle cx="50" cy="50" r="12" fill="#2a2140" opacity="0.85" />}
        {id === 'mush' && <g fill="#fff"><circle cx="32" cy="30" r="6" /><circle cx="64" cy="24" r="5" /><circle cx="74" cy="40" r="4" /></g>}
        <g transform={`translate(${fx - 50} ${fy - 52})`}>
          <ellipse cx="40" cy="48" rx="6" ry="7.5" fill="#1b1430" />
          <ellipse cx="60" cy="48" rx="6" ry="7.5" fill="#1b1430" />
          <circle cx="42" cy="45.5" r="2.2" fill="#fff" />
          <circle cx="62" cy="45.5" r="2.2" fill="#fff" />
          {m === 'smile' && <path d="M41 60q9 8 18 0" stroke="#1b1430" strokeWidth="4" strokeLinecap="round" fill="none" />}
          {m === 'grin' && <path d="M39 58h22q-2 11-11 11t-11-11z" fill="#1b1430" />}
          {m === 'o' && <ellipse cx="50" cy="62" rx="5" ry="6" fill="#1b1430" />}
          {m === 'smug' && <path d="M42 62q10 4 17-4" stroke="#1b1430" strokeWidth="4" strokeLinecap="round" fill="none" />}
          <ellipse cx="33" cy="58" rx="5" ry="3" fill="#ff7aa8" opacity="0.5" />
          <ellipse cx="67" cy="58" rx="5" ry="3" fill="#ff7aa8" opacity="0.5" />
        </g>
      </g>
    </svg>
  );
}
